// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC } from "react";

// eslint-disable-next-line spire/fenced-imports
import { css } from "styled-components";

const enum fields {
    headerText = "headerText",
    subHeaderText = "subHeaderText",
    imageUrl = "imageUrl",
}

export interface HeaderWrapperStyles {
    headerWrapperStyles: InjectableCss;
    gridContainerStyles: InjectableCss;
    gridItemHeaderImageStyles: InjectableCss;
}

export const headerWrapperStyles: HeaderWrapperStyles = {
    headerWrapperStyles: {
        css: css`
            background: #f1f1f1;
            margin-bottom: 15px;
            ${breakpointMediaQueries(baseTheme, [
                css`
                    margin-left: -20px;
                    margin-right: -20px;
                `,
                css`
                    margin-left: -20px;
                    margin-right: -20px;
                `,
                css``,
                css``,
                css``,
            ])}
        `,
    },
    gridContainerStyles: {
        css: css`
            ${breakpointMediaQueries(baseTheme, [
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 51px;
                    margin-right: 51px;
                `,
            ])}
            @media print {
                max-width: 100%;
            }
        `,
    },
    gridItemHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
            padding-top: 0px;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headerText]: string;
        [fields.subHeaderText]: string;
        [fields.imageUrl]: string;
    };
}

type Props = OwnProps;

const styles = headerWrapperStyles;
const ContentHeader: FC<Props> = ({ fields, ...otherProps }) => {
    return (
        <StyledWrapper
            {...styles.headerWrapperStyles}
            data-test-selector="custom_content_header"
            className="custom_content_wrapper"
        >
            <GridContainer
                style={{
                    paddingBottom: "15px",
                    maxWidth: "1400px",
                    margin: "0 auto",
                    paddingLeft: "15px",
                    paddingTop: "0px",
                    paddingRight: "0px",
                }}
                {...styles.gridContainerStyles}
                className="custom_content_parent_grid"
            >
                <GridItem width={[10, 10, 12, 5, 5]}>
                    <GridContainer>
                        <GridItem width={12}>
                            <Typography variant="h1" style={{ fontSize: "32px" }}>
                                {fields.headerText}
                            </Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <Typography style={{ fontSize: "16px", fontWeight: 500, lineHeight: "22px" }}>
                                {fields.subHeaderText}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem width={[0, 0, 0, 1, 1]}></GridItem>
                <GridItem
                    width={[10, 10, 12, 6, 6]}
                    {...styles.gridItemHeaderImageStyles}
                    className="custom_content_header_image_grid"
                >
                    <img
                        src={fields.imageUrl}
                        alt={translate("Content Header Image")}
                        className="custom_content_header_image_image"
                        style={{ width: "94%", height: "auto" }}
                    />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentHeader,
    definition: {
        displayName: "Content Header",
        icon: "Grid",
        fieldDefinitions: [
            {
                name: fields.headerText,
                displayName: "Header Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The header text.",
                placeholder: "Header Text Placeholder",
                isRequired: true,
            },
            {
                name: fields.subHeaderText,
                displayName: "Subheader Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The sub-heading text for the header widget.",
                placeholder: "Sub-heading text placeholder.",
                isRequired: false,
            },
            {
                name: fields.imageUrl,
                displayName: "Header Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
